import * as React from 'react';
import { ChevronDown, Users } from 'lucide-react';
import { useEquipeInventario, type ContadorInventario } from '@/hooks/inventario';
import { cn, numero } from '@/lib/utils';
import { haQuanto } from './semaforo';

/**
 * Quem contou — por crachá, somando escopos e contagens.
 *
 * O PBS grava o crachá em cada coleta e não mostra em lugar nenhum. Aqui
 * aparece quem trabalhou, quanto, e quando foi o último envio. O coletor
 * manda tudo no "Encerrar", então último envio é o fim da sessão da pessoa,
 * não o último bipe.
 */
const ROTULO_CONTAGEM = ['', '1ª', 'Recont.', 'Final'];

export function PainelEquipe({
  inventario,
  escopo,
  compacto = false,
}: {
  inventario: number;
  escopo?: number | undefined;
  compacto?: boolean;
}): JSX.Element | null {
  const consulta = useEquipeInventario(inventario);
  const [aberto, definirAberto] = React.useState(!compacto);

  const linhas = (consulta.data?.equipe ?? []).filter((l) => escopo === undefined || l.escopo === escopo);
  if (consulta.isLoading || linhas.length === 0) return null;

  const pessoas = new Map<string, { nome: string | null; linhas: ContadorInventario[] }>();
  for (const l of linhas) {
    const p = pessoas.get(l.cracha) ?? { nome: l.nome, linhas: [] };
    p.linhas.push(l);
    pessoas.set(l.cracha, p);
  }
  const lista = [...pessoas.entries()]
    .map(([cracha, p]) => ({
      cracha,
      nome: p.nome,
      enderecos: p.linhas.reduce((s, l) => s + l.enderecos, 0),
      itens: p.linhas.reduce((s, l) => s + l.itens, 0),
      unidades: p.linhas.reduce((s, l) => s + l.unidades, 0),
      ultimo: p.linhas.map((l) => l.ultimoEnvio).filter((d): d is string => !!d).sort().at(-1) ?? null,
      contagens: [...new Set(p.linhas.map((l) => l.contagem))].sort(),
      escopos: [...new Set(p.linhas.map((l) => l.escopo))].sort((a, b) => a - b),
    }))
    .sort((a, b) => b.enderecos - a.enderecos);
  const maior = Math.max(1, ...lista.map((p) => p.enderecos));

  return (
    <div className={cn(!compacto && 'rounded-xl border border-borda bg-superficie shadow-xs')}>
      <button
        type="button"
        onClick={() => definirAberto((v) => !v)}
        className={cn('flex w-full items-center gap-2 text-left', compacto ? 'py-1' : 'px-4 py-3')}
      >
        <Users className="h-4 w-4 text-textoFraco" />
        <span className="text-sm font-semibold">Equipe</span>
        <span className="text-xs text-textoFraco">
          {numero(lista.length)} {lista.length === 1 ? 'pessoa contou' : 'pessoas contaram'}
        </span>
        <ChevronDown className={cn('ml-auto h-4 w-4 text-textoFraco transition-transform', aberto && 'rotate-180')} />
      </button>
      {aberto ? (
        <div className={cn('overflow-x-auto', !compacto && 'border-t border-borda')}>
          <table className="w-full text-xs">
            <thead className="bg-superficie2 text-left text-textoFraco">
              <tr>
                <th className="px-3 py-1.5 font-medium">Quem</th>
                <th className="px-3 py-1.5 font-medium">Endereços</th>
                <th className="px-3 py-1.5 text-right font-medium">Itens</th>
                <th className="px-3 py-1.5 text-right font-medium">Unidades</th>
                <th className="px-3 py-1.5 font-medium">Contagens</th>
                {escopo === undefined ? <th className="px-3 py-1.5 font-medium">Escopos</th> : null}
                <th className="px-3 py-1.5 font-medium">Último envio</th>
              </tr>
            </thead>
            <tbody>
              {lista.map((p) => (
                <tr key={p.cracha} className="border-t border-borda">
                  <td className="px-3 py-1.5">
                    <span className="font-medium">{p.nome ?? 'sem nome'}</span>
                    <span className="ml-1 font-mono text-textoSuave">#{p.cracha}</span>
                  </td>
                  <td className="px-3 py-1.5">
                    <div className="flex items-center gap-2">
                      <span className="tabular w-8 text-right font-semibold">{numero(p.enderecos)}</span>
                      <span className="h-1.5 w-20 overflow-hidden rounded-full bg-superficie3">
                        <span
                          className="gradiente-marca block h-full rounded-full"
                          style={{ width: `${(p.enderecos / maior) * 100}%` }}
                        />
                      </span>
                    </div>
                  </td>
                  <td className="tabular px-3 py-1.5 text-right">{numero(p.itens)}</td>
                  <td className="tabular px-3 py-1.5 text-right">{numero(p.unidades)}</td>
                  <td className="px-3 py-1.5">{p.contagens.map((c) => ROTULO_CONTAGEM[c] ?? c).join(', ')}</td>
                  {escopo === undefined ? (
                    <td className="tabular px-3 py-1.5 text-textoFraco">{p.escopos.join(', ')}</td>
                  ) : null}
                  <td className="px-3 py-1.5 text-textoFraco">{haQuanto(p.ultimo)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ) : null}
    </div>
  );
}
